import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecipeLineage, getRecipeCookingSessions, uploadImage, getRecipeById } from '../services/api';
import { useAuth } from '../context/authContext';
import { Text } from './ui';
import SignedImage from './SignedImage';
import sessionReportService from '../services/sessionReportService';
import RecipeDiffModal from './RecipeDiffModal';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

// Layout configuration for tidy tree algorithm (horizontal layout)
const LAYOUT_CONFIG = {
    nodeWidth: 180,
    nodeHeight: 220,
    levelGap: 280,      // Horizontal spacing between levels (left to right)
    minSiblingGap: 40,   // Vertical spacing between siblings
    padding: 12,
    rootX: 100,         // Root starts on the left
    rootY: 100,         // Vertical center position
    snap: 1,             // Grid alignment for crisp edges
    marginY: 100,       // Top margin
    cardWidth: 180,      // Card width for overlap resolution (matches nodeWidth)
    horizontalGap: 24,   // Minimum horizontal gap between cards
};

// Edge routing configuration
const EDGE_CONFIG = {
    FORK_EDGE: {
        stroke: colorTokens.neutral[900],
        strokeWidth: 2,
        opacity: 0.6,
        strokeDasharray: undefined
    },
    MERGE_EDGE: {
        stroke: colorTokens.neutral[900],
        strokeWidth: 1,
        opacity: 0.4,
        strokeDasharray: '5,5'
    },
    CURVE_OFFSET: 30,
};

/**
 * Tidy Tree Layout Algorithm (Reingold-Tilford)
 * Provides optimal spacing, overlap prevention, and parent centering
 */

// Type definitions for tidy tree nodes
const TNode = {
    id: String,
    children: Array,
    // Output coordinates
    x: Number,
    y: Number,
    // Internal algorithm state
    mod: Number,
    thread: Object,
    ancestor: Object,
    change: Number,
    shift: Number,
    number: Number,
    prelim: Number,
    // Dimensions
    w: Number,
    h: Number,
    // Parent reference
    parent: Object
};

/**
 * Build tree structure from nodes and edges
 */
function buildTree(nodes, edges) {
    const nodeMap = new Map();
    
    // Create node map with children array
    nodes.forEach(node => {
        nodeMap.set(node.id, { 
            ...node, 
            children: [],
            w: LAYOUT_CONFIG.nodeWidth,
            h: LAYOUT_CONFIG.nodeHeight
        });
    });
    
    // Build parent-child relationships from edges
    edges.forEach(edge => {
        if (edge.type === 'fork') {
            const parent = nodeMap.get(edge.from);
            const child = nodeMap.get(edge.to);
            if (parent && child) {
                parent.children.push(child);
            }
        }
    });
    
    // Find root node - prioritize is_original, then no parent_recipe_id, then earliest created
    let root = nodes.find(node => node.is_original);
    if (!root) {
        root = nodes.find(node => !node.parent_recipe_id);
    }
    if (!root) {
        // If still no root found, use the earliest created node as root
        root = nodes.reduce((earliest, current) => {
            const earliestDate = new Date(earliest.created_at);
            const currentDate = new Date(current.created_at);
            return currentDate < earliestDate ? current : earliest;
        });
    }
    if (!root) {
        throw new Error('No root node found in the tree');
    }
    
    return {
        root: nodeMap.get(root.id),
        nodeMap
    };
}

/**
 * Tidy tree layout algorithm implementation
 */
function layoutTidy(root, config = LAYOUT_CONFIG) {
    const c = { snap: 1, ...config };
    
    // Build parent map for algorithm helpers
    const parentMap = new Map();
    const buildParents = (n) => {
        if (!n) return;
        n.children?.forEach(ch => { 
            parentMap.set(ch, n); 
            buildParents(ch); 
        });
    };
    root.parent = undefined;
    buildParents(root);
    
    // Helper functions
    function leftSibling(v) {
        const p = parentMap.get(v);
        if (!p) return undefined;
        const i = p.children.indexOf(v);
        return i > 0 ? p.children[i - 1] : undefined;
    }
    
    function siblingsFirstChild(p) { 
        if (!p || !p.children || !Array.isArray(p.children) || p.children.length === 0) return null;
        return p.children[0]; 
    }
    
    function nextLeft(v) { 
        return v?.children?.[0] || v?.thread || null; 
    }
    
    function nextRight(v) {
        const ch = v?.children; 
        return ch && ch.length ? ch[ch.length - 1] : v?.thread || null;
    }
    
    function ancestor(vil, v, defaultAncestor) {
        const p = parentMap.get(v);
        return (p?.children.includes(vil.ancestor)) ? vil.ancestor : defaultAncestor;
    }
    
    function separation(a, b) {
        // Gap between subtree boxes: half heights + min gap (vertical spacing for horizontal layout)
        const hA = a.h ?? c.nodeHeight;
        const hB = b.h ?? c.nodeHeight;
        return (hA / 2) + (hB / 2) + c.minSiblingGap;
    }
    
    function snap(x) { 
        return Math.round(x / c.snap) * c.snap; 
    }
    
    // First walk - calculate preliminary positions
    const firstWalk = (v, depth = 0, defaultAncestor = v) => {
        v.ancestor = v.ancestor ?? v;
        v.change = v.change ?? 0;
        v.shift = v.shift ?? 0;
        v.thread = v.thread ?? null;
        v.number = v.number ?? (v.parent ? v.parent.children.indexOf(v) + 1 : 1);
        
        // Horizontal layout: x increases with depth (left to right), y is calculated by prelim
        v.x = c.rootX + depth * c.levelGap;
        const kids = v.children || [];
        
        if (kids.length === 0) {
            // Leaf node
            const left = leftSibling(v);
            v.prelim = left ? left.prelim + separation(left, v) : 0;
        } else {
            let defaultAnc = kids[0];
            kids.forEach(child => firstWalk(child, depth + 1, defaultAnc));
            apportion(v, defaultAncestor);
            
            const left = kids[0];
            const right = kids[kids.length - 1];
            const mid = (left.prelim + right.prelim) / 2;
            const leftSib = leftSibling(v);
            
            if (leftSib) {
                v.prelim = leftSib.prelim + separation(leftSib, v);
                v.mod = v.prelim - mid;
            } else {
                v.prelim = mid;
            }
        }
    };
    
    // Apportion function - handles subtree positioning
    const apportion = (v, defaultAncestor) => {
        const w = leftSibling(v);
        if (!w) return;
        
        let vir = v, vor = v;
        let vil = w, vol = v.parent ? siblingsFirstChild(v.parent) : null;
        let sir = v.mod || 0, sor = v.mod || 0;
        let sil = vil ? (vil.mod || 0) : 0, sol = vol ? (vol.mod || 0) : 0;
        
        while (nextRight(vil) && nextLeft(vir)) {
            vil = nextRight(vil);
            vir = nextLeft(vir);
            vol = nextLeft(vol);
            vor = nextRight(vor);
            
            // Add null checks before accessing properties
            if (!vil || !vir || !vol || !vor) break;
            
            vor.ancestor = v;
            
            const shift = (vil.prelim + sil) - (vir.prelim + sir) + separation(vil, vir);
            if (shift > 0) {
                const a = ancestor(vil, v, defaultAncestor);
                moveSubtree(a, v, shift);
                sir += shift;
                sor += shift;
            }
            sil += vil.mod || 0;
            sir += vir.mod || 0;
            sol += vol.mod || 0;
            sor += vor.mod || 0;
        }
        
        if (nextRight(vil) && !nextRight(vor) && vor) {
            vor.thread = nextRight(vil);
            vor.mod = (vor.mod || 0) + (sil - sor);
        }
        if (nextLeft(vir) && !nextLeft(vol) && vol) {
            vol.thread = nextLeft(vir);
            vol.mod = (vol.mod || 0) + (sir - sol);
        }
    };
    
    // Move subtree function
    const moveSubtree = (wl, wr, shift) => {
        const subtrees = wr.number - wl.number;
        wr.change = (wr.change || 0) + shift / subtrees;
        wr.mod = (wr.mod || 0) + shift;
        wl.change = (wl.change || 0) - shift / subtrees;
    };
    
    // Execute shifts function
    const executeShifts = (v) => {
        let shift = 0, change = 0;
        const kids = v.children || [];
        for (let i = kids.length - 1; i >= 0; i--) {
            const w = kids[i];
            w.prelim = (w.prelim || 0) + shift;
            w.mod = (w.mod || 0) + shift;
            change += w.change || 0;
            shift += w.shift || 0 + change;
        }
    };
    
    // Second walk - final positioning (horizontal layout: x from depth, y from prelim)
    const secondWalk = (v, m = 0, depth = 0) => {
        v.y = snap(c.rootY + (v.prelim || 0) + m);
        executeShifts(v);
        (v.children || []).forEach(ch => secondWalk(ch, m + (v.mod || 0), depth + 1));
    };
    
    // Assign final dimensions and depth
    const assignDims = (n, depth = 0) => {
        n.depth = depth; // Store depth for overlap resolution
        n.x = c.rootX + depth * c.levelGap;
        n.w = n.w ?? c.nodeWidth;
        n.h = n.h ?? c.nodeHeight;
        n.children?.forEach(ch => assignDims(ch, depth + 1));
    };
    
    // Run the algorithm
    firstWalk(root, 0, root);
    secondWalk(root, 0, 0);
    assignDims(root);
    
    // Post-processing: Resolve overlaps by shifting subtrees horizontally
    resolveOverlaps(root, c);
    
    return root;
}

/**
 * Resolve overlaps by shifting parent subtrees horizontally in card-width chunks
 * Only adjusts horizontal positioning when overlaps are detected
 * When nodes overlap, shift the entire parent branch (subtree) to the right
 */
function resolveOverlaps(root, config) {
    const CARD_WIDTH = config.cardWidth;
    const H_GAP = config.horizontalGap;
    const CARD_HEIGHT = config.nodeHeight;
    const V_GAP = config.minSiblingGap;
    const LEVEL_GAP = config.levelGap;
    
    // Build parent map to find ancestors
    const parentMap = new Map();
    function buildParentMap(node, parent = null) {
        if (parent) {
            parentMap.set(node, parent);
        }
        if (node.children) {
            node.children.forEach(child => buildParentMap(child, node));
        }
    }
    buildParentMap(root);
    
    // Collect all nodes with their bounding boxes
    const allNodes = [];
    function collectAllNodes(node) {
        const nodeWidth = node.w ?? CARD_WIDTH;
        const nodeHeight = node.h ?? CARD_HEIGHT;
        allNodes.push({
            node,
            left: node.x - nodeWidth / 2,
            right: node.x + nodeWidth / 2,
            top: node.y - nodeHeight / 2,
            bottom: node.y + nodeHeight / 2,
            depth: node.depth ?? 0
        });
        if (node.children) {
            node.children.forEach(child => collectAllNodes(child));
        }
    }
    collectAllNodes(root);
    
    // Helper: Shift a node and all its descendants vertically
    function shiftSubtree(node, deltaY) {
        node.y += deltaY;
        if (node.children) {
            node.children.forEach(child => shiftSubtree(child, deltaY));
        }
    }
    
    // Helper: Find the root of the branch containing this node
    // (the node at the same depth level that is the ancestor of this node)
    function findBranchRoot(node, targetDepth) {
        let current = node;
        while (current && (current.depth ?? 0) > targetDepth) {
            current = parentMap.get(current);
        }
        return current;
    }
    
    // Sort nodes by x (depth), then by y
    allNodes.sort((a, b) => {
        if (Math.abs(a.node.x - b.node.x) < 1) {
            // Same depth, sort by y
            return a.node.y - b.node.y;
        }
        return a.node.x - b.node.x;
    });
    
    // Check for overlaps and shift parent subtrees
    // We'll iterate multiple times to handle cascading overlaps
    let hasOverlaps = true;
    let iterations = 0;
    const maxIterations = 10; // Prevent infinite loops
    
    while (hasOverlaps && iterations < maxIterations) {
        hasOverlaps = false;
        iterations++;
        
        // Recalculate bounding boxes after shifts
        allNodes.forEach(item => {
            const nodeWidth = item.node.w ?? CARD_WIDTH;
            const nodeHeight = item.node.h ?? CARD_HEIGHT;
            item.left = item.node.x - nodeWidth / 2;
            item.right = item.node.x + nodeWidth / 2;
            item.top = item.node.y - nodeHeight / 2;
            item.bottom = item.node.y + nodeHeight / 2;
        });
        
        // Sort again after position updates
        allNodes.sort((a, b) => {
            if (Math.abs(a.node.x - b.node.x) < 1) {
                return a.node.y - b.node.y;
            }
            return a.node.x - b.node.x;
        });
        
        for (let i = 0; i < allNodes.length - 1; i++) {
            const current = allNodes[i];
            const next = allNodes[i + 1];
            
            // Check if bounding boxes overlap
            const horizontalOverlap = (current.right + H_GAP) - next.left;
            const verticalOverlap = (current.bottom + V_GAP) - next.top;
            
            if (horizontalOverlap > 0 && verticalOverlap > 0) {
                // Overlap detected - shift the entire parent branch containing the next node vertically
                // Find the parent of the next node (the branch that needs to be shifted)
                let branchToShift = parentMap.get(next.node);
                
                // If nodes are at the same depth, they're siblings - shift the parent's branch
                // If they're at different depths, find the parent at the collision depth
                const currentDepth = current.depth ?? 0;
                const nextDepth = next.depth ?? 0;
                
                if (currentDepth === nextDepth) {
                    // Same depth - they're siblings, shift the parent branch
                    branchToShift = parentMap.get(next.node);
                } else {
                    // Different depths - find the parent at the shallower depth
                    // This is the branch root that needs shifting
                    branchToShift = findBranchRoot(next.node, currentDepth);
                }
                
                // If we found a branch to shift (and it's not the root)
                if (branchToShift && branchToShift !== root) {
                    // Small vertical adjustment - just enough to clear the overlap plus gap
                    // Use the actual vertical overlap amount plus the gap, not a large fixed amount
                    const shiftAmount = verticalOverlap + V_GAP;
                    
                    // Shift the entire branch starting from the parent vertically
                    shiftSubtree(branchToShift, shiftAmount);
                    hasOverlaps = true;
                } else if (branchToShift === root) {
                    // If it's the root branch, shift the next node's immediate parent instead
                    const nextParent = parentMap.get(next.node);
                    if (nextParent && nextParent !== root) {
                        const shiftAmount = verticalOverlap + V_GAP;
                        shiftSubtree(nextParent, shiftAmount);
                        hasOverlaps = true;
                    }
                }
            }
        }
    }
}

/**
 * Main layout function
 */
function layoutTree(nodes, edges) {
    try {
        const tree = buildTree(nodes, edges);
        const positionedRoot = layoutTidy(tree.root, LAYOUT_CONFIG);
        
        // Collect all positioned nodes
        const positionedNodes = [];
        function collectNodes(node) {
            positionedNodes.push({
                ...node,
                x: Math.round(node.x),
                y: Math.round(node.y),
                width: node.w,
                height: node.h
            });
            
            if (node.children) {
                node.children.forEach(child => collectNodes(child));
            }
        }
        
        collectNodes(positionedRoot);
        
        return {
            nodes: positionedNodes,
            success: true
        };
    } catch (error) {
        console.error('Layout error:', error);
        return {
            nodes: [],
            success: false,
            error: error.message
        };
    }
}

/**
 * Orthogonal Edge Routing
 * Creates clean, straight-line connections between nodes
 * Uses the original simple approach for better visual consistency
 */
function edgePolyline(parent, child) {
    // Horizontal layout: parent on left, child on right
    // Nodes are positioned with left: node.x, top: node.y and centered with transform: translate(-50%, -50%)
    // So the actual center of the node is at (node.x, node.y)
    const x1 = parent.x + parent.width / 2;  // Right edge of parent
    const x2 = child.x - child.width / 2;    // Left edge of child
    const midX = (x1 + x2) / 2;
    
    return [
        [x1, parent.y],      // Start at right edge of parent (center vertically)
        [midX, parent.y],    // Go horizontally to middle
        [midX, child.y],     // Go vertically to child's level
        [x2, child.y],       // Go horizontally to left edge of child
    ];
}

function generateOrthogonalPath(fromNode, toNode, edgeType = 'fork') {
    const points = edgePolyline(fromNode, toNode);
    
    // Convert points to SVG path string
    const path = points.reduce((path, point, index) => {
        const command = index === 0 ? 'M' : 'L';
        return `${path} ${command} ${point[0]} ${point[1]}`;
    }, '');
    
    return {
        path,
        fromPoint: { x: points[0][0], y: points[0][1] },
        toPoint: { x: points[points.length - 1][0], y: points[points.length - 1][1] },
        points
    };
}

function routeEdges(nodes, edges) {
    const routedEdges = [];
    
    edges.forEach(edge => {
        const fromNode = nodes.find(n => n.id === edge.from);
        const toNode = nodes.find(n => n.id === edge.to);
        
        if (!fromNode || !toNode) {
            console.warn('Missing node for edge:', edge.id, 'from:', edge.from, 'to:', edge.to);
            return;
        }
        
        const pathData = generateOrthogonalPath(fromNode, toNode, edge.type);
        
        const routedEdge = {
            ...edge,
            path: pathData.path,
            fromPoint: pathData.fromPoint,
            toPoint: pathData.toPoint,
            points: pathData.points,
            style: edge.type === 'merge' ? EDGE_CONFIG.MERGE_EDGE : EDGE_CONFIG.FORK_EDGE
        };
        
        routedEdges.push(routedEdge);
    });
    
    return routedEdges;
}

/**
 * Session Lineage Viewer Component
 */
const SessionLineageViewer = ({ recipeId, user }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchSessions();
    }, [recipeId, user]);

    const fetchSessions = async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            const sessionsData = await getRecipeCookingSessions(recipeId, token);
            console.log('Fetched sessions data:', sessionsData);
            setSessions(sessionsData);
        } catch (err) {
            console.error('Error fetching sessions:', err);
            setError('Failed to load cooking sessions');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        }
        return `${minutes}m`;
    };

    const handleImageUpload = async (file, type, targetId = null) => {
        if (!file) return;
        
        try {
            setUploading(true);
            
            if (!user) {
                throw new Error('User not authenticated');
            }
            
            const token = await user.getIdToken();
            if (!token) {
                throw new Error('Failed to get authentication token');
            }
            
            // Upload the image
            const uploadedImageUrl = await uploadImage(file, token);
            console.log('Uploaded image URL:', uploadedImageUrl);
            
            // Create photo entry
            const photoEntry = {
                url: uploadedImageUrl,
                timestamp: new Date().toISOString(),
                caption: file.name,
                trigger: 'manual_upload'
            };
            console.log('Created photo entry:', photoEntry);
            
            // Update the report based on type
            let updatedReport = { ...selectedSession.session_report };
            
            if (type === 'timeline' && targetId) {
                // Add to timeline entry
                const timelineIndex = updatedReport.timeline.findIndex(entry => entry.id === targetId);
                if (timelineIndex !== -1) {
                    // Ensure photos array exists
                    if (!updatedReport.timeline[timelineIndex].photos) {
                        updatedReport.timeline[timelineIndex].photos = [];
                    }
                    updatedReport.timeline[timelineIndex].photos.push(photoEntry);
                }
            } else if (type === 'highlight' && targetId) {
                // Add to highlight
                const highlightIndex = updatedReport.highlights.findIndex(highlight => highlight.id === targetId);
                if (highlightIndex !== -1) {
                    // Ensure photos array exists
                    if (!updatedReport.highlights[highlightIndex].photos) {
                        updatedReport.highlights[highlightIndex].photos = [];
                    }
                    updatedReport.highlights[highlightIndex].photos.push(photoEntry);
                }
            } else if (type === 'final_outcome') {
                // Set as dish photo
                updatedReport.final_outcome.dish_photo = photoEntry;
            }
            
            // Save the updated report
            console.log('Saving updated report:', updatedReport);
            await sessionReportService.updateSessionReport(selectedSession.id, updatedReport, token);
            
            // Update the selected session with the new report
            setSelectedSession(prev => ({
                ...prev,
                session_report: updatedReport
            }));
            
            console.log('Report updated successfully');
            
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (event, type, targetId = null) => {
        const file = event.target.files[0];
        if (file) {
            handleImageUpload(file, type, targetId);
        }
        // Reset the input
        event.target.value = '';
    };

    const handleDeletePhoto = async (type, targetId = null, photoIndex = null) => {
        if (!user) {
            alert('User not authenticated');
            return;
        }

        try {
            const token = await user.getIdToken();
            let updatedReport = { ...selectedSession.session_report };
            
            if (type === 'timeline' && targetId !== null && photoIndex !== null) {
                // Remove photo from timeline entry
                const timelineIndex = updatedReport.timeline.findIndex(entry => entry.id === targetId);
                if (timelineIndex !== -1) {
                    updatedReport.timeline[timelineIndex].photos.splice(photoIndex, 1);
                }
            } else if (type === 'highlight' && targetId !== null && photoIndex !== null) {
                // Remove photo from highlight
                const highlightIndex = updatedReport.highlights.findIndex(highlight => highlight.id === targetId);
                if (highlightIndex !== -1) {
                    updatedReport.highlights[highlightIndex].photos.splice(photoIndex, 1);
                }
            } else if (type === 'final_outcome') {
                // Remove dish photo
                updatedReport.final_outcome.dish_photo = null;
            }
            
            // Save the updated report
            await sessionReportService.updateSessionReport(selectedSession.id, updatedReport, token);
            
            // Update the selected session with the new report
            setSelectedSession(prev => ({
                ...prev,
                session_report: updatedReport
            }));
            
        } catch (error) {
            console.error('Error deleting photo:', error);
            alert('Failed to delete photo. Please try again.');
        }
    };

    const handleTextEdit = async (type, targetId, field, value) => {
        if (!user) {
            alert('User not authenticated');
            return;
        }

        try {
            const token = await user.getIdToken();
            let updatedReport = { ...selectedSession.session_report };
            
            if (type === 'timeline') {
                const timelineIndex = updatedReport.timeline.findIndex(entry => entry.id === targetId);
                if (timelineIndex !== -1) {
                    updatedReport.timeline[timelineIndex][field] = value;
                }
            } else if (type === 'highlight') {
                const highlightIndex = updatedReport.highlights.findIndex(highlight => highlight.id === targetId);
                if (highlightIndex !== -1) {
                    updatedReport.highlights[highlightIndex][field] = value;
                }
            } else if (type === 'final_outcome') {
                if (!updatedReport.final_outcome) {
                    updatedReport.final_outcome = {};
                }
                updatedReport.final_outcome[field] = value;
            }
            
            // Save the updated report
            await sessionReportService.updateSessionReport(selectedSession.id, updatedReport, token);
            
            // Update the selected session with the new report
            setSelectedSession(prev => ({
                ...prev,
                session_report: updatedReport
            }));
            
        } catch (error) {
            console.error('Error updating text:', error);
            alert('Failed to update text. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-neutral-900 opacity-60">Loading sessions...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-red-600">{error}</Text>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-neutral-900 opacity-60">Login to view cooking sessions</Text>
            </div>
        );
    }

    if (sessions.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-neutral-900 opacity-60">No cooking sessions found</Text>
            </div>
        );
    }

    return (
        <div className="h-full w-full overflow-y-auto">
            <div className="space-y-4 p-4">
                {sessions.map((session, index) => (
                    <div
                        key={session.id}
                        className="bg-neutral-0 hairline p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                        onClick={() => setSelectedSession(session)}
                    >
                        <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    SESSION {sessions.length - index}
                                </Text>
                                {session.was_completed && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-mono uppercase tracking-wider">
                                        COMPLETED
                                    </span>
                                )}
                            </div>
                            <Text className="text-xs text-neutral-900 opacity-60">
                                {formatDate(session.created_at)}
                            </Text>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-1">
                                    DURATION
                                </Text>
                                <Text className="text-neutral-900">
                                    {formatDuration(session.actual_duration_seconds)}
                                </Text>
                            </div>
                            <div>
                                <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-1">
                                    STEPS
                                </Text>
                                <Text className="text-neutral-900">
                                    {session.total_steps} steps
                                </Text>
                            </div>
                        </div>

                        {session.reflection_comments && (
                            <div className="mt-3 pt-3 border-t border-neutral-200">
                                <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-1">
                                    REFLECTION
                                </Text>
                                <Text className="text-sm text-neutral-900 line-clamp-2">
                                    {session.reflection_comments}
                                </Text>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Session Details Modal */}
            {selectedSession && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-neutral-0 hairline max-w-2xl w-full max-h-[90vh] overflow-hidden relative">
                        <button 
                            onClick={() => setSelectedSession(null)}
                            className="absolute top-4 right-4 z-10 text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                        >
                            ✕
                        </button>
                        
                        <div className="p-6 overflow-y-auto max-h-[90vh]">
                            <div className="mb-6">
                                <Text className="font-sans text-2xl font-medium text-neutral-900 mb-2">
                                    Session Details
                                </Text>
                                <Text className="text-sm text-neutral-900 opacity-60">
                                    {formatDate(selectedSession.created_at)}
                                </Text>
                            </div>

                            <div className="grid grid-cols-2 gap-6 mb-6">
                                <div>
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        STATUS
                                    </Text>
                                    <Text className="text-neutral-900">
                                        {selectedSession.was_completed ? 'Completed' : 'Incomplete'}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        DURATION
                                    </Text>
                                    <Text className="text-neutral-900">
                                        {formatDuration(selectedSession.actual_duration_seconds)}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        TOTAL STEPS
                                    </Text>
                                    <Text className="text-neutral-900">
                                        {selectedSession.total_steps}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        ESTIMATED TIME
                                    </Text>
                                    <Text className="text-neutral-900">
                                        {selectedSession.total_duration_minutes} minutes
                                    </Text>
                                </div>
                            </div>

                            {/* New Session Report Display */}
                            {selectedSession.session_report && (
                                <div className="mb-6">
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-4">
                                        SESSION REPORT
                                    </Text>
                                    
                                    {/* Final Outcome Photo */}
                                    <div className="mb-4">
                                        <Text className="text-sm font-medium text-neutral-900 mb-2">Final Dish</Text>
                                        {selectedSession.session_report.final_outcome && selectedSession.session_report.final_outcome.dish_photo && (
                                            <div className="relative inline-block mb-2">
                                                <img
                                                    src={selectedSession.session_report.final_outcome.dish_photo.url}
                                                    alt="Final dish"
                                                    className="w-48 h-48 object-cover rounded border"
                                                    onError={(e) => {
                                                        console.warn('Failed to load final dish photo:', selectedSession.session_report.final_outcome.dish_photo.url);
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                                <button
                                                    onClick={() => handleDeletePhoto('final_outcome')}
                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                                    title="Delete photo"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Upload button for final dish photo */}
                                        <div>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileSelect(e, 'final_outcome')}
                                                className="hidden"
                                                id="final-dish-upload"
                                                disabled={uploading}
                                            />
                                            <label
                                                htmlFor="final-dish-upload"
                                                className="inline-flex items-center px-4 py-2 text-sm font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors cursor-pointer disabled:opacity-50"
                                            >
                                                {uploading ? 'Uploading...' : '📷 Upload Final Dish Photo'}
                                            </label>
                                        </div>
                                    </div>
                                    
                                    {/* Final Outcome Comments */}
                                    <div className="mb-4">
                                        <Text className="text-sm font-medium text-neutral-900 mb-2">Comments</Text>
                                        <textarea
                                            value={selectedSession.session_report.final_outcome?.comments || ''}
                                            onChange={(e) => {
                                                const updatedReport = { ...selectedSession.session_report };
                                                if (!updatedReport.final_outcome) {
                                                    updatedReport.final_outcome = {};
                                                }
                                                updatedReport.final_outcome.comments = e.target.value;
                                                
                                                // Update the selected session immediately for UI responsiveness
                                                setSelectedSession(prev => ({
                                                    ...prev,
                                                    session_report: updatedReport
                                                }));
                                                
                                                // Save to backend
                                                handleTextEdit('final_outcome', null, 'comments', e.target.value);
                                            }}
                                            placeholder="Add final comments about the dish..."
                                            className="w-full p-3 border border-neutral-300 rounded text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            rows={3}
                                        />
                                    </div>
                                    
                                    {/* Timeline Entries */}
                                    {selectedSession.session_report.timeline && selectedSession.session_report.timeline.length > 0 && (
                                        <div className="mb-4">
                                            <Text className="text-sm font-medium text-neutral-900 mb-2">Timeline</Text>
                                            <div className="space-y-3">
                                                {selectedSession.session_report.timeline.map((entry, index) => (
                                                    <div key={index} className="bg-neutral-50 p-3">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60">
                                                                {entry.phase || 'Step'} {index + 1}
                                                            </Text>
                                                            <Text className="text-xs text-neutral-900 opacity-60">
                                                                {new Date(entry.timestamp).toLocaleTimeString()}
                                                            </Text>
                                                        </div>
                                                        
                                                        {/* Editable Notes */}
                                                        <div className="mb-2">
                                                            <Text className="text-xs font-medium text-neutral-900 mb-1">Notes:</Text>
                                                            <textarea
                                                                value={entry.notes || ''}
                                                                onChange={(e) => handleTextEdit('timeline', entry.id, 'notes', e.target.value)}
                                                                placeholder="Add notes about this step..."
                                                                className="w-full p-2 border border-neutral-300 rounded text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                rows={2}
                                                            />
                                                        </div>
                                                        
                                                        {entry.photos && entry.photos.length > 0 && (
                                                            <div className="flex gap-2 mb-2 flex-wrap">
                                                                {entry.photos.map((photo, photoIndex) => (
                                                                    <div key={photoIndex} className="relative">
                                                                        <img
                                                                            src={photo.url}
                                                                            alt={photo.caption || `Photo ${photoIndex + 1}`}
                                                                            className="w-16 h-16 object-cover rounded border"
                                                                            onError={(e) => {
                                                                                console.warn('Failed to load timeline photo:', photo.url);
                                                                                e.target.style.display = 'none';
                                                                            }}
                                                                        />
                                                                        <button
                                                                            onClick={() => handleDeletePhoto('timeline', entry.id, photoIndex)}
                                                                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                                                            title="Delete photo"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Upload button for timeline entry */}
                                                        <div className="mb-2">
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={(e) => handleFileSelect(e, 'timeline', entry.id)}
                                                                className="hidden"
                                                                id={`timeline-upload-${entry.id}`}
                                                                disabled={uploading}
                                                            />
                                                            <label
                                                                htmlFor={`timeline-upload-${entry.id}`}
                                                                className="inline-flex items-center px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors cursor-pointer disabled:opacity-50"
                                                            >
                                                                {uploading ? 'Uploading...' : '📷 Add Photo'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Highlights */}
                                    {selectedSession.session_report.highlights && selectedSession.session_report.highlights.length > 0 && (
                                        <div className="mb-4">
                                            <Text className="text-sm font-medium text-neutral-900 mb-2">Highlights</Text>
                                            <div className="space-y-3">
                                                {selectedSession.session_report.highlights.map((highlight, index) => (
                                                    <div key={index} className="bg-neutral-50 p-3">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60">
                                                                {highlight.type || 'Highlight'} {index + 1}
                                                            </Text>
                                                            <Text className="text-xs text-neutral-900 opacity-60">
                                                                {new Date(highlight.timestamp).toLocaleTimeString()}
                                                            </Text>
                                                        </div>
                                                        
                                                        {/* Editable Description */}
                                                        <div className="mb-2">
                                                            <Text className="text-xs font-medium text-neutral-900 mb-1">Description:</Text>
                                                            <textarea
                                                                value={highlight.description || ''}
                                                                onChange={(e) => handleTextEdit('highlight', highlight.id, 'description', e.target.value)}
                                                                placeholder="Describe this highlight..."
                                                                className="w-full p-2 border border-neutral-300 rounded text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                rows={2}
                                                            />
                                                        </div>
                                                        
                                                        {highlight.photos && highlight.photos.length > 0 && (
                                                            <div className="flex gap-2 mb-2 flex-wrap">
                                                                {highlight.photos.map((photo, photoIndex) => (
                                                                    <div key={photoIndex} className="relative">
                                                                        <img
                                                                            src={photo.url}
                                                                            alt={photo.caption || `Photo ${photoIndex + 1}`}
                                                                            className="w-16 h-16 object-cover rounded border"
                                                                            onError={(e) => {
                                                                                console.warn('Failed to load highlight photo:', photo.url);
                                                                                e.target.style.display = 'none';
                                                                            }}
                                                                        />
                                                                        <button
                                                                            onClick={() => handleDeletePhoto('highlight', highlight.id, photoIndex)}
                                                                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                                                            title="Delete photo"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Upload button for highlight */}
                                                        <div className="mb-2">
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={(e) => handleFileSelect(e, 'highlight', highlight.id)}
                                                                className="hidden"
                                                                id={`highlight-upload-${highlight.id}`}
                                                                disabled={uploading}
                                                            />
                                                            <label
                                                                htmlFor={`highlight-upload-${highlight.id}`}
                                                                className="inline-flex items-center px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors cursor-pointer disabled:opacity-50"
                                                            >
                                                                {uploading ? 'Uploading...' : '📷 Add Photo'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Fallback to legacy data if no session report */}
                            {!selectedSession.session_report && selectedSession.reflection_comments && (
                                <div className="mb-6">
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        REFLECTION
                                    </Text>
                                    <div className="bg-neutral-50 p-4">
                                        <Text className="text-neutral-900">
                                            {selectedSession.reflection_comments}
                                        </Text>
                                    </div>
                                </div>
                            )}

                            {!selectedSession.session_report && selectedSession.session_photos && selectedSession.session_photos.length > 0 && (
                                <div className="mb-6">
                                    <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                        SESSION PHOTOS
                                    </Text>
                                    <div className="grid grid-cols-2 gap-4">
                                        {selectedSession.session_photos.map((photo, index) => (
                                            <img
                                                key={index}
                                                src={photo}
                                                alt={`Session photo ${index + 1}`}
                                                className="w-full h-32 object-cover bg-neutral-50"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Main LineageViewer Component
 */
const LineageViewerV2 = ({ recipeId, onRecipeSelect, currentRecipeId, onCompareModeChange, compareMode: externalCompareMode, showCompareSelectionModal = false, onToggleCompareRecipe, refreshTrigger }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [lineage, setLineage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [focusedNodeIndex, setFocusedNodeIndex] = useState(0);
    const [positionedNodes, setPositionedNodes] = useState([]);
    const [routedEdges, setRoutedEdges] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const pinchStartRef = useRef(null);
    const lastPinchDistanceRef = useRef(null);
    const containerRef = useRef(null);
    const wheelContainerRef = useRef(null);
    const nodeRefs = useRef([]);
    const [internalCompareMode, setInternalCompareMode] = useState(false);
    const compareMode = externalCompareMode !== undefined ? externalCompareMode : internalCompareMode;
    const setCompareMode = externalCompareMode !== undefined && onCompareModeChange 
        ? onCompareModeChange 
        : setInternalCompareMode;
    const [selectedCompare, setSelectedCompare] = useState([]); // store up to 2 recipe objects
    const [diffOpen, setDiffOpen] = useState(false);
    const [loadingComparison, setLoadingComparison] = useState(false);

    // function to handle compare node selection
    const handleCompareSelect = async (node) => {
      if (!compareMode) return;
      
      // Sync with CompareContext via onToggleCompareRecipe
      if (onToggleCompareRecipe) {
        onToggleCompareRecipe(node);
      }
      
      // Also maintain local state for visual feedback
      const existingIndex = selectedCompare.findIndex((r) => r.id === node.id);
      if (existingIndex !== -1) {
        // Deselect by removing from array
        const newSelection = selectedCompare.filter((r) => r.id !== node.id);
        setSelectedCompare(newSelection);
        return;
      }
      
      // Add to selection (max 2)
      const next = [...selectedCompare, node].slice(0, 2);
      setSelectedCompare(next);
    };

    // when diff modal closes reset selection
    useEffect(() => {
      if (!diffOpen) {
        setSelectedCompare([]);
        if (externalCompareMode !== undefined && onCompareModeChange) {
          onCompareModeChange(false);
        } else {
          setInternalCompareMode(false);
        }
      }
    }, [diffOpen, externalCompareMode, onCompareModeChange]);

    useEffect(() => {
        fetchLineage();
    }, [recipeId, user]);

    // Refresh lineage when refreshTrigger changes (e.g., when modal opens or after session creation)
    useEffect(() => {
        if (refreshTrigger !== undefined) {
            fetchLineage();
        }
    }, [refreshTrigger]);

    useEffect(() => {
        if (lineage && lineage.recipes) {
            const currentIndex = lineage.recipes.findIndex(r => r.id === parseInt(currentRecipeId));
            if (currentIndex !== -1) {
                setFocusedNodeIndex(currentIndex);
            }
        }
    }, [lineage, currentRecipeId]);

    useEffect(() => {
        if (positionedNodes.length > 0) {
            // Center the map initially - use requestAnimationFrame for continuous updates
            requestAnimationFrame(() => {
                centerMap();
            });
        }
    }, [positionedNodes]);

    // Add global mouse event listeners for dragging
    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            if (isDragging) {
                setViewOffset({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y
                });
            }
        };

        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [isDragging, dragStart]);

    // Add wheel event listener with passive: false to allow preventDefault
    useEffect(() => {
        const container = wheelContainerRef.current;
        if (!container) return;

        const handleWheelEvent = (e) => {
            // Prevent default to stop page scrolling when zooming
            e.preventDefault();
            // Subtle zoom with mouse wheel (not very sensitive)
            const zoomSensitivity = 0.001; // Very subtle
            const delta = e.deltaY * zoomSensitivity;
            setZoom(prevZoom => {
                const newZoom = Math.max(0.5, Math.min(2, prevZoom - delta));
                return newZoom;
            });
        };

        container.addEventListener('wheel', handleWheelEvent, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheelEvent);
        };
    }, []);

    useEffect(() => {
        if (lineage && lineage.recipes) {
            // Convert lineage data to nodes and edges - KEEP ALL RECIPE FIELDS
            const nodes = lineage.recipes.map(recipe => ({
                ...recipe, // Include all recipe fields for comparison
                images: recipe.images || []
            }));

            // Build proper tree structure with all relationships
            const edges = [];
            const nodeMap = new Map();
            
            // Create node map for easy lookup
            nodes.forEach(node => {
                nodeMap.set(node.id, { ...node, children: [] });
            });
            
            // Build parent-child relationships
            nodes.forEach(node => {
                if (node.parent_recipe_id) {
                    // Check if parent exists in our nodeMap
                    if (nodeMap.has(node.parent_recipe_id)) {
                        const parent = nodeMap.get(node.parent_recipe_id);
                        const child = nodeMap.get(node.id);
                        parent.children.push(child);
                        
                        edges.push({
                            id: `edge-${node.parent_recipe_id}-${node.id}`,
                            from: node.parent_recipe_id,
                            to: node.id,
                            type: 'fork'
                        });
                    } else {
                        // Parent not in our data - this shouldn't happen with the new backend query
                        // but we'll log it for debugging
                        console.warn('Parent recipe not found in lineage data:', {
                            childId: node.id,
                            childName: node.name,
                            parentId: node.parent_recipe_id
                        });
                    }
                }
            });

            // Layout the tree using the nodeMap with proper children structure
            const layoutResult = layoutTree(Array.from(nodeMap.values()), edges);
            if (layoutResult.success) {
                setPositionedNodes(layoutResult.nodes);
                
                // Route edges
                const routedEdges = routeEdges(layoutResult.nodes, edges);
                setRoutedEdges(routedEdges);
                
                // Auto-center the tree after layout and reset zoom
                // Use requestAnimationFrame for smooth, continuous updates
                requestAnimationFrame(() => {
                    centerMap();
                    setZoom(1); // Reset zoom when tree is laid out
                });
            } else {
                setError('Failed to layout tree: ' + layoutResult.error);
            }
        }
    }, [lineage]);

    const fetchLineage = async () => {
        try {
            setLoading(true);
            setError(null);
            const lineageData = await getRecipeLineage(recipeId);
            setLineage(lineageData);
        } catch (err) {
            console.error('Error fetching lineage:', err);
            setError('Failed to load recipe lineage');
        } finally {
            setLoading(false);
        }
    };


    const centerMap = () => {
        if (positionedNodes.length > 0 && containerRef.current) {
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            
            // Calculate the bounding box of all nodes
            const minX = Math.min(...positionedNodes.map(n => n.x - n.width / 2));
            const maxX = Math.max(...positionedNodes.map(n => n.x + n.width / 2));
            const minY = Math.min(...positionedNodes.map(n => n.y - n.height / 2));
            const maxY = Math.max(...positionedNodes.map(n => n.y + n.height / 2));
            
            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;
            
            // For horizontal layout, position root on left with margin, center vertically
            const offsetX = LAYOUT_CONFIG.rootX - minX;  // Position root at rootX
            const offsetY = (containerRect.height / 2) - centerY;
            
            setViewOffset({ x: offsetX, y: offsetY });
        }
    };

    const scrollToNode = (index) => {
        if (nodeRefs.current[index] && containerRef.current) {
            const node = positionedNodes[index];
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            
            // For horizontal layout, center node vertically, maintain horizontal position
            const offsetX = LAYOUT_CONFIG.rootX - (node.x - LAYOUT_CONFIG.rootX);
            const offsetY = (containerRect.height / 2) - node.y;
            
            setViewOffset({ x: offsetX, y: offsetY });
        }
    };

    const handleNodeClick = (recipe) => {
        // If compare selection modal is open, select recipe for comparison instead of navigating
        if (showCompareSelectionModal && onToggleCompareRecipe) {
            onToggleCompareRecipe(recipe);
            return;
        }
        
        if (compareMode) {
            handleCompareSelect(recipe);
            return;
        }
        const isPublic = recipe.is_public;
        const isOwner = user && recipe.owner_id === user.uid;
        
        if (!isPublic && !isOwner) {
            // Show tooltip for private recipes
            alert('🔒 This recipe is private and cannot be viewed.');
            return;
        }
        
        // For now, allow navigation to all public recipes
        // TODO: Add friend relationship checks when friends API is available
        // const isFriend = checkFriendRelationship(recipe.owner_id);
        // if (!isPublic && !isOwner && !isFriend) {
        //     alert('🔒 This recipe is only visible to friends of the author.');
        //     return;
        // }
        
        if (onRecipeSelect) {
            // CRITICAL: Update focusedNodeIndex immediately to prevent two rings showing
            // This ensures isCurrent and isFocused point to the same node
            const newIndex = positionedNodes.findIndex(n => n.id === recipe.id);
            if (newIndex !== -1) {
                setFocusedNodeIndex(newIndex);
            }
            onRecipeSelect(recipe);
        } else {
            navigate(`/recipes/${recipe.id}`);
        }
    };

    const handleMouseDown = (e) => {
        // Don't start drag if clicking on a node card
        if (e.target.closest('.lineage-node-card')) {
            return;
        }
        
        // Start drag on background, container, or empty space
        setIsDragging(true);
        setDragStart({
            x: e.clientX - viewOffset.x,
            y: e.clientY - viewOffset.y
        });
        e.preventDefault();
    };


    const handleWheel = (e) => {
        // Subtle zoom with mouse wheel (not very sensitive)
        const zoomSensitivity = 0.001; // Very subtle
        const delta = e.deltaY * zoomSensitivity;
        const newZoom = Math.max(0.5, Math.min(2, zoom - delta));
        setZoom(newZoom);
    };

    const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            // Pinch gesture started
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const distance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            );
            pinchStartRef.current = { distance, zoom };
            lastPinchDistanceRef.current = distance;
            e.preventDefault();
        } else if (e.touches.length === 1) {
            // Single touch - allow dragging
            const touch = e.touches[0];
            setIsDragging(true);
            setDragStart({
                x: touch.clientX - viewOffset.x,
                y: touch.clientY - viewOffset.y
            });
        }
    };

    const handleTouchMove = (e) => {
        if (e.touches.length === 2 && pinchStartRef.current) {
            // Pinch gesture
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const distance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            );
            
            if (lastPinchDistanceRef.current) {
                const delta = distance - lastPinchDistanceRef.current;
                const zoomSensitivity = 0.01; // Very subtle zoom
                const zoomDelta = delta * zoomSensitivity;
                const newZoom = Math.max(0.5, Math.min(2, pinchStartRef.current.zoom + zoomDelta));
                setZoom(newZoom);
            }
            
            lastPinchDistanceRef.current = distance;
            e.preventDefault();
        } else if (e.touches.length === 1 && isDragging) {
            // Single touch drag
            const touch = e.touches[0];
            setViewOffset({
                x: touch.clientX - dragStart.x,
                y: touch.clientY - dragStart.y
            });
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e) => {
        if (e.touches.length < 2) {
            // Pinch ended
            pinchStartRef.current = null;
            lastPinchDistanceRef.current = null;
        }
        if (e.touches.length === 0) {
            // All touches ended
            setIsDragging(false);
        }
    };

    const handleKeyDown = (event) => {
        if (!positionedNodes || positionedNodes.length === 0) return;

        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                if (focusedNodeIndex > 0) {
                    const newIndex = focusedNodeIndex - 1;
                    setFocusedNodeIndex(newIndex);
                    scrollToNode(newIndex);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (focusedNodeIndex < positionedNodes.length - 1) {
                    const newIndex = focusedNodeIndex + 1;
                    setFocusedNodeIndex(newIndex);
                    scrollToNode(newIndex);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                // Find the closest node above the current one
                const currentNode = positionedNodes[focusedNodeIndex];
                if (currentNode) {
                    const nodesAbove = positionedNodes.filter((node, index) => 
                        index !== focusedNodeIndex && 
                        node.y < currentNode.y &&
                        Math.abs(node.x - currentNode.x) < 200
                    );
                    if (nodesAbove.length > 0) {
                        const closestAbove = nodesAbove.reduce((closest, node) => 
                            Math.abs(node.x - currentNode.x) < Math.abs(closest.x - currentNode.x) ? node : closest
                        );
                        const newIndex = positionedNodes.findIndex(n => n.id === closestAbove.id);
                        setFocusedNodeIndex(newIndex);
                        scrollToNode(newIndex);
                    }
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                // Find the closest node below the current one
                const currentNodeDown = positionedNodes[focusedNodeIndex];
                if (currentNodeDown) {
                    const nodesBelow = positionedNodes.filter((node, index) => 
                        index !== focusedNodeIndex && 
                        node.y > currentNodeDown.y &&
                        Math.abs(node.x - currentNodeDown.x) < 200
                    );
                    if (nodesBelow.length > 0) {
                        const closestBelow = nodesBelow.reduce((closest, node) => 
                            Math.abs(node.x - currentNodeDown.x) < Math.abs(closest.x - currentNodeDown.x) ? node : closest
                        );
                        const newIndex = positionedNodes.findIndex(n => n.id === closestBelow.id);
                        setFocusedNodeIndex(newIndex);
                        scrollToNode(newIndex);
                    }
                }
                break;
            case 'Enter':
                event.preventDefault();
                if (positionedNodes[focusedNodeIndex]) {
                    handleNodeClick(positionedNodes[focusedNodeIndex]);
                }
                break;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-neutral-900 opacity-60">Loading lineage...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-red-600">{error}</Text>
            </div>
        );
    }

    // Check if we have fork lineage (more than 1 recipe) or at least 1 recipe
    const hasForkLineage = lineage && lineage.recipes && lineage.recipes.length > 1;
    const hasSingleRecipe = lineage && lineage.recipes && lineage.recipes.length === 1;
    
    // If no recipes at all, show message
    if (!lineage) {
        return (
            <div className="flex items-center justify-center h-full">
                <Text className="text-neutral-900 opacity-60">No recipe lineage found</Text>
            </div>
        );
    }

    return (
        <div className="h-full w-full flex flex-col" style={{ position: 'relative', zIndex: 1 }}>
            {/* Forking Lineage Content */}
            <div className="flex-1 overflow-hidden" style={{ position: 'relative', zIndex: 1 }}>
                {(hasForkLineage || hasSingleRecipe) ? (
                    <div 
                        ref={wheelContainerRef}
                        className="h-full w-full relative overflow-hidden select-none"
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                    >
                        {/* Dot Grid Background */}
                        <div 
                            className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: `
                                    radial-gradient(circle, ${colorTokens.neutral[900]} 1px, transparent 1px)
                                `,
                                backgroundSize: '20px 20px',
                                backgroundPosition: `${viewOffset.x}px ${viewOffset.y}px`
                            }}
                        />
                        
                        {/* Draggable Container */}
                        <div 
                            ref={containerRef}
                            className="h-full w-full relative"
                            style={{
                                transform: `translate(${viewOffset.x}px, ${viewOffset.y}px) scale(${zoom})`,
                                transformOrigin: '0 0',
                                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                cursor: isDragging ? 'grabbing' : 'grab'
                            }}
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            {/* Draggable Background - extends far to the left and right */}
                            <div 
                                className="absolute lineage-background"
                                onMouseDown={handleMouseDown}
                                onTouchStart={handleTouchStart}
                                style={{ 
                                    left: '-5000px',
                                    top: '-5000px',
                                    width: '10000px',
                                    height: '10000px',
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    zIndex: 0
                                }}
                            />
                            
                            <>
                                {/* Edge Connections - extend indefinitely */}
                                <svg 
                                    className="absolute pointer-events-none"
                                    style={{
                                        width: '1000000px',
                                        height: '1000000px',
                                        left: 0,
                                        top: 0,
                                        overflow: 'visible'
                                    }}
                                >
                                    {routedEdges && routedEdges.length > 0 ? routedEdges.map((edge) => {
                                        if (!edge || !edge.path) return null;
                                        return (
                                            <path
                                                key={edge.id || `edge-${edge.from}-${edge.to}`}
                                                d={edge.path}
                                                stroke={edge.style?.stroke || colorTokens.neutral[900]}
                                                strokeWidth={edge.style?.strokeWidth || 2}
                                                fill="none"
                                                opacity={edge.style?.opacity || 0.6}
                                                strokeDasharray={edge.style?.strokeDasharray || undefined}
                                            />
                                        );
                                    }) : null}
                                </svg>

                                {/* Recipe Nodes */}
                                {positionedNodes.map((node, index) => {
                                    const isCurrent = node.id === parseInt(currentRecipeId);
                                    const isFocused = index === focusedNodeIndex;
                                    const isPublic = node.is_public;
                                    const isOwner = user && node.owner_id === user.uid;
                                    const ownerIsPublic = node.owner_is_public ?? true; // Default to true if not set
                                    const ownerIsAdmin = node.owner_is_admin ?? false; // Default to false if not set
                                    
                                    // Recipe is accessible if:
                                    // 1. User is the owner, OR
                                    // 2. Recipe is public AND (owner account is public OR owner is admin)
                                    const canVisit = isOwner || (isPublic && (ownerIsPublic || ownerIsAdmin));
                                    
                                    // Recipe should be grayed out if it's from a private account and user is not the owner
                                    const isPrivateAccountRecipe = !isOwner && !ownerIsPublic && !ownerIsAdmin;
                                    
                                    const isSelected = compareMode && selectedCompare.find((r) => r.id === node.id);
                                    
                                    return (
                                        <div
                                            key={node.id}
                                            ref={el => nodeRefs.current[index] = el}
                                            className={`absolute transition-all duration-200 lineage-node-card ${canVisit ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                            style={{
                                                left: `${node.x}px`,
                                                top: `${node.y}px`,
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 10
                                            }}
                                            onClick={() => {
                                                if (canVisit) {
                                                    handleNodeClick(node);
                                                }
                                            }}
                                            onFocus={() => setFocusedNodeIndex(index)}
                                        >
                                            {/* Node Card - Only show ONE ring: isCurrent (blue selected) takes precedence over isFocused (black keyboard) */}
                                            <div 
                                                className={`
                                                    w-44 h-56 bg-neutral-0 transition-all duration-200 relative
                                                    ${isCurrent ? 'ring-2 ring-blue-500' : (isFocused ? 'ring-2 ring-neutral-900' : '')}
                                                    ${canVisit ? 'hover:shadow-lg hover:scale-105 cursor-pointer' : 'opacity-60 cursor-not-allowed'}
                                                    ${!isPublic || isPrivateAccountRecipe ? 'bg-neutral-50' : ''}
                                                `}
                                                style={{
                                                    border: '1px solid #0D0D0D',
                                                    boxShadow: isSelected ? '0 0 0 4px rgba(27, 38, 59, 0.3), 0 0 0 8px rgba(27, 38, 59, 0.15), 0 4px 8px rgba(0, 0, 0, 0.2)' : undefined
                                                }}
                                            >
                                                {/* Gold Standard Border - positioned outside to not interfere with selection */}
                                                {node.is_gold_standard && (
                                                    <div 
                                                        className="absolute inset-0 pointer-events-none"
                                                        style={{
                                                            border: '2px solid #FFD700',
                                                            margin: '-2px',
                                                            borderRadius: '0'
                                                        }}
                                                    />
                                                )}
                                                {/* Recipe Image */}
                                                <div className="w-full h-32 bg-neutral-50 relative overflow-hidden">
                                                    <SignedImage
                                                        src={node.images && node.images.length > 0 ? node.images[0] : null}
                                                        alt={node.name}
                                                        className="w-full h-full object-cover"
                                                        category={node.category}
                                                    />
                                                    {isCurrent && (
                                                        <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
                                                    )}
                                                </div>
                                                
                                                {/* Recipe Info */}
                                                <div className="p-3 h-24 flex flex-col justify-between">
                                                    <Text className="text-sm font-medium text-neutral-900 leading-tight line-clamp-2">
                                                        {node.name}
                                                    </Text>
                                                    <div className="flex justify-between items-center text-xs text-neutral-900 opacity-60">
                                                        <span className="truncate">{node.created_by_name}</span>
                                                        <div className="flex gap-1">
                                                            {!isPublic && (
                                                                <span className="text-red-600">🔒</span>
                                                            )}
                                                            {isPrivateAccountRecipe && (
                                                                <span className="text-neutral-400" title="Private account">🔒</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <Text className="text-neutral-900 opacity-60">No recipe lineage found</Text>
                    </div>
                )}
            </div>
            
            {compareMode && !diffOpen && (
              <div className="absolute bottom-10 right-1 bg-neutral-50 hairline p-2 text-xs font-mono uppercase tracking-wider">
                {selectedCompare.length}/2 selected
              </div>
            )}
            
            {loadingComparison && (
              <div className="absolute bottom-10 right-1 bg-neutral-50 hairline p-3 text-xs font-mono uppercase tracking-wider">
                Loading comparison...
              </div>
            )}

            {/* Diff modal */}
            <RecipeDiffModal
              isOpen={diffOpen && selectedCompare.length === 2}
              onClose={() => setDiffOpen(false)}
              originalRecipe={selectedCompare[0] || {}}
              updatedRecipe={selectedCompare[1] || {}}
            />
        </div>
    );
};

export default LineageViewerV2;
